import type {
  ApprovalResultDto,
  ChangeRequestResultDto,
  PanelSnapshotDto,
  RegenerateResultDto,
  RejectResultDto,
} from '@devmil/contracts';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {useReauth} from '../auth/ReauthContext.js';
import {
  isPanelApiError,
  PanelApiError,
  type PanelDataSource,
} from '../data/PanelDataSource.js';
import {describeProblem} from '../lib/panel-problem.js';

export const PANEL_SNAPSHOT_QUERY_KEY = ['panel', 'snapshot'] as const;

/**
 * De quanto em quanto tempo a aba pergunta se a regeneração terminou.
 *
 * Cinco segundos. A reexecução leva MINUTOS (geração, sete portões e
 * renderização pelo meio), então um intervalo curto não adianta a resposta;
 * o que ele compra é a peça aparecer logo depois de ficar pronta, em vez de
 * até cinco segundos depois de a próxima abertura da tela acontecer.
 */
export const INTERVALO_DA_REGENERACAO_MS = 5_000;

/**
 * Por quanto tempo a aba acompanha, antes de desistir e DIZER que desistiu.
 *
 * Dez minutos. O renderizador tem teto de quinze
 * (`TIMEOUT_PADRAO_MS`, em `spool-media-renderer.ts`), e uma peça de 30 s
 * conclui em 145 s medidos; dez minutos cobrem o caso normal com folga sem
 * deixar uma aba esquecida batendo na API a noite inteira.
 *
 * Estourar o teto NÃO é falha da geração, e a mensagem diz isso: a execução
 * pode continuar e terminar bem. O que acabou foi a espera desta aba, e o botão
 * de atualizar fica ali para quem quiser conferir.
 */
export const TETO_DA_REGENERACAO_MS = 10 * 60_000;

/**
 * A regeneração que esta aba está acompanhando.
 *
 * Uma só por vez, e é o suficiente: regenerar é a ação mais cara da tela, e o
 * dono a dispara sobre a peça que está lendo. Um segundo clique substitui o
 * acompanhamento, porque é a segunda peça que ele está esperando.
 */
export interface RegeneracaoEmCurso {
  contentVersionId: string;
  /** Nulo quando o dia não tinha execução para reexecutar. */
  runId: string | null;
  /** O instante do PEDIDO, pelo relógio do servidor. */
  requestedAt: string;
  estado: 'aguardando' | 'sem_execucao' | 'tempo_esgotado' | 'falhou';
}

export interface PanelToast {
  message: string;
  tone: 'success' | 'error';
}

export interface PanelContextValue {
  /**
   * A porta, para as telas que não vivem do snapshot.
   *
   * O detalhe de uma conversa e os pedidos de privacidade leem por
   * identificador da URL, então não cabem no snapshot único do painel. Elas
   * continuam falando com o mundo só por esta porta: expor a porta é diferente
   * de abrir um segundo caminho de rede.
   */
  dataSource: PanelDataSource;
  snapshot: PanelSnapshotDto | undefined;
  /**
   * O erro da busca do snapshot, quando houve. O shell precisa distinguir
   * "ainda carregando" de "a API recusou": um 401 aqui é a porta de entrada
   * da tela de login no modo HTTP, e engolir o erro deixaria o painel preso
   * no "Carregando…" para sempre.
   */
  snapshotError: unknown;
  /**
   * Pausa efetiva. Vive aqui porque AppShell, TodayPage e OperationPage leem o
   * mesmo valor, e porque a pausa precisa valer nas três no mesmo instante.
   */
  paused: boolean;
  toast: PanelToast | null;
  showToast: (message: string | null, tone?: PanelToast['tone']) => void;
  /**
   * `justification` é a ressalva escrita, e ela só existe quando algum portão
   * da peça está em alerta. Ausente vale `null`, que é o que peça limpa manda.
   */
  approveContent: (
    contentVersionId: string,
    justification?: string | null,
  ) => Promise<ApprovalResultDto>;
  requestChange: (
    contentVersionId: string,
    comment: string,
  ) => Promise<ChangeRequestResultDto>;
  rejectContent: (
    contentVersionId: string,
    comment: string,
  ) => Promise<RejectResultDto>;
  /**
   * Regenerar a peça, com ou sem troca de assunto.
   *
   * `changeTopic` é obrigatório aqui, como no contrato: um padrão implícito
   * faria a caixa desmarcada e a caixa esquecida mandarem a mesma coisa, e
   * ninguém saberia qual das duas o dono quis.
   */
  regenerateContent: (
    contentVersionId: string,
    reason: string,
    changeTopic: boolean,
  ) => Promise<RegenerateResultDto>;
  /**
   * A regeneração que esta aba está acompanhando, ou `null`.
   *
   * Ela existe porque a rota devolve uma versão VAZIA e a peça volta minutos
   * depois. Até 2026-09-06 o painel mostrava um toast e nunca mais falava do
   * assunto (achado F12): a peça saía da lista, a aba ficava parada, e uma
   * falha de geração não aparecia em lugar nenhum.
   */
  regeneracao: RegeneracaoEmCurso | null;
  /** Relê o retrato agora. É a atualização MANUAL que o plano pede. */
  atualizarAgora: () => void;
  togglePause: () => Promise<void>;
}

const PanelContext = createContext<PanelContextValue | null>(null);

export const usePanel = (): PanelContextValue => {
  const value = useContext(PanelContext);
  if (!value) {
    throw new Error('usePanel precisa de um <PanelProvider> acima.');
  }
  return value;
};

/** Chave de idempotência gerada no clique, nunca no render. */
const newIdempotencyKey = (): string => crypto.randomUUID();

const messageOf = (error: unknown): string => {
  if (isPanelApiError(error)) {
    return error.payload.message;
  }
  return error instanceof Error
    ? error.message
    : 'Não foi possível concluir a ação.';
};

export const PanelProvider = ({
  dataSource,
  children,
}: {
  dataSource: PanelDataSource;
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const reauth = useReauth();
  const [toast, setToast] = useState<PanelToast | null>(null);

  const {data: snapshot, error: snapshotError} = useQuery({
    queryKey: PANEL_SNAPSHOT_QUERY_KEY,
    queryFn: ({signal}) => dataSource.getSnapshot(signal),
  });

  /**
   * As ações, alcançáveis de dentro delas mesmas.
   *
   * O diálogo de reauth repete a ação recusada, e a repetição precisa passar
   * pelo caminho INTEIRO de novo (pausa, otimismo, toast), não só pela porta.
   * Um `useCallback` não consegue se referenciar durante a própria definição,
   * então a referência vai por este ref, preenchido logo após as definições.
   */
  const acoesRef = useRef<{
    approveContent?: (
      contentVersionId: string,
      justification?: string | null,
    ) => Promise<unknown>;
    requestChange?: (
      contentVersionId: string,
      comment: string,
    ) => Promise<unknown>;
    rejectContent?: (
      contentVersionId: string,
      comment: string,
    ) => Promise<unknown>;
    regenerateContent?: (
      contentVersionId: string,
      reason: string,
      changeTopic: boolean,
    ) => Promise<unknown>;
    togglePause?: () => Promise<void>;
  }>({});

  const showToast = useCallback(
    (message: string | null, tone: PanelToast['tone'] = 'success') => {
      setToast(message === null ? null : {message, tone});
    },
    [],
  );

  const [regeneracao, setRegeneracao] = useState<RegeneracaoEmCurso | null>(
    null,
  );

  const atualizarAgora = useCallback(() => {
    void queryClient.invalidateQueries({queryKey: PANEL_SNAPSHOT_QUERY_KEY});
  }, [queryClient]);

  /**
   * O ACOMPANHAMENTO da regeneração: leitura com intervalo e teto.
   *
   * ## Como ele sabe que acabou
   *
   * Comparando `finishedAt` da execução com o `requestedAt` do pedido, os dois
   * pelo relógio do SERVIDOR. Olhar só o `status` não serve, e as duas
   * armadilhas estão medidas no código de hoje: a regeneração devolve o dia de
   * `ready` para `partial` ANTES de disparar a reexecução, e `partial` também é
   * um desfecho terminal; e aquele mesmo `update` não zera `finished_at`, então
   * o carimbo que está lá quando o toast aparece é o da execução ANTERIOR.
   *
   * ## Por que ele desiste
   *
   * Porque uma aba aberta e esquecida bateria nesta rota para sempre. Desistir
   * NÃO é dizer que falhou: o estado `tempo_esgotado` diz que a espera desta
   * aba acabou, e a execução pode muito bem terminar depois. O botão de
   * atualizar continua ali.
   *
   * ## Por que um `setInterval`, e não `useQuery` com `refetchInterval`
   *
   * Porque o que termina o laço não é uma resposta em particular, e sim uma
   * comparação entre duas coisas que só esta função tem: o carimbo do pedido e
   * o da execução. Um `useQuery` daria cache e chave para uma leitura que não é
   * cacheável (a resposta muda sozinha) e ninguém mais consome.
   */
  useEffect(() => {
    if (!regeneracao || regeneracao.estado !== 'aguardando') {
      return;
    }
    const {runId, requestedAt, contentVersionId} = regeneracao;
    if (runId === null) {
      return;
    }
    const limite = Date.now() + TETO_DA_REGENERACAO_MS;
    let vivo = true;

    const perguntar = async () => {
      let estado;
      try {
        estado = await dataSource.getRunStatus(runId);
      } catch {
        // A leitura falhou nesta volta. Não vira toast: a rede pisca, e um
        // aviso por pisco ensinaria o dono a ignorar avisos. O teto lá embaixo
        // é quem fecha o assunto se a falha for permanente.
        return;
      }
      if (!vivo) {
        return;
      }
      const terminou =
        estado.finishedAt !== null &&
        Date.parse(estado.finishedAt) > Date.parse(requestedAt);
      if (!terminou) {
        return;
      }
      // O retrato inteiro é relido: a reexecução mexeu no dia, e não só nesta
      // peça. É a mesma decisão do `invalidateQueries` que já existia depois da
      // regeneração, agora acontecendo na hora certa.
      void queryClient.invalidateQueries({queryKey: PANEL_SNAPSHOT_QUERY_KEY});
      if (estado.status === 'failed') {
        setRegeneracao({...regeneracao, estado: 'falhou'});
        showToast(
          'A geração da peça falhou. A versão nova ficou vazia, e o runbook editorial explica onde olhar.',
          'error',
        );
        return;
      }
      setRegeneracao(null);
      showToast('A peça regenerada voltou para a fila.');
    };

    const tique = setInterval(() => {
      if (Date.now() >= limite) {
        setRegeneracao((atual) =>
          atual?.contentVersionId === contentVersionId
            ? {...atual, estado: 'tempo_esgotado'}
            : atual,
        );
        return;
      }
      void perguntar();
    }, INTERVALO_DA_REGENERACAO_MS);

    return () => {
      vivo = false;
      clearInterval(tique);
    };
  }, [dataSource, queryClient, regeneracao, showToast]);

  const patch = useCallback(
    (update: (current: PanelSnapshotDto) => PanelSnapshotDto) => {
      queryClient.setQueryData<PanelSnapshotDto>(
        PANEL_SNAPSHOT_QUERY_KEY,
        (current) => (current ? update(current) : current),
      );
    },
    [queryClient],
  );

  /**
   * Atualização otimista reversível: guarda o estado anterior, aplica o
   * esperado e, se a porta recusar, devolve exatamente o que estava lá antes e
   * anuncia o erro.
   *
   * Quando a recusa é "confirme o passkey" e existe porta de autenticação, o
   * anúncio não é um toast morto: é o diálogo de reautenticação, com a ação
   * recusada pendurada para repetir depois da confirmação, se o dono quiser.
   * Sem porta (fixture e dublês), o comportamento é o de sempre.
   */
  const optimistic = useCallback(
    async <T,>(
      update: (current: PanelSnapshotDto) => PanelSnapshotDto,
      call: () => Promise<T>,
      retry?: () => void,
    ): Promise<T> => {
      const previous = queryClient.getQueryData<PanelSnapshotDto>(
        PANEL_SNAPSHOT_QUERY_KEY,
      );
      patch(update);
      try {
        return await call();
      } catch (error) {
        if (previous) {
          queryClient.setQueryData(PANEL_SNAPSHOT_QUERY_KEY, previous);
        }
        /*
         * "A peça mudou" obriga LEITURA NOVA, e não só um aviso.
         *
         * Devolver o retrato anterior e parar por aí deixaria na tela
         * exatamente o cartão que a API acabou de recusar, com o mesmo hash
         * velho: o dono clicaria de novo e tomaria o mesmo `409` para sempre. A
         * invalidação troca o retrato pelo estado atual, e o próximo clique já
         * sai com a identidade que o servidor reconhece.
         */
        if (
          isPanelApiError(error) &&
          error.payload.code === 'MANIFEST_CHANGED'
        ) {
          void queryClient.invalidateQueries({
            queryKey: PANEL_SNAPSHOT_QUERY_KEY,
          });
        }
        const problema = describeProblem(error);
        if (reauth.armado && problema.missingProof === 'passkey_reauth') {
          reauth.oferecer({message: problema.message, retry: retry ?? null});
        } else {
          showToast(messageOf(error), 'error');
        }
        throw error;
      }
    },
    [patch, queryClient, reauth, showToast],
  );

  /**
   * Falha fechada no provider, e não só no botão desabilitado: se alguma
   * chamada escapar da UI, ela para aqui, antes de qualquer atualização
   * otimista. Contador não pode piscar um número que não vai valer.
   */
  const refuseWhilePaused = useCallback(() => {
    const current = queryClient.getQueryData<PanelSnapshotDto>(
      PANEL_SNAPSHOT_QUERY_KEY,
    );
    if (!current?.operation.paused) {
      return;
    }
    throw new PanelApiError({
      code: 'blocked_while_paused',
      message: 'Operação pausada. Retome a operação para decidir.',
      requestId: newIdempotencyKey(),
      fieldErrors: null,
    });
  }, [queryClient]);

  const approveContent = useCallback(
    async (contentVersionId: string, justification?: string | null) => {
      refuseWhilePaused();
      const ressalva = justification ?? null;
      /*
       * O hash sai do RETRATO QUE A TELA DESENHOU, e é lido antes do otimismo.
       *
       * É o que faz "aprovar o que eu li" ser literal: o mesmo objeto que
       * produziu o cartão produz o valor que sobe. Ler depois do `patch` do
       * otimismo leria o retrato já remendado por este próprio clique.
       *
       * Sem hash, a ação nem sai daqui. `null` no DTO significa que o servidor
       * não conseguiu selar a versão ou que ela não espera mais decisão, e nos
       * dois casos mandar a aprovação só renderia um erro mais confuso lá.
       */
      const aprovacao = aprovacaoDoCartao(
        queryClient.getQueryData<PanelSnapshotDto>(PANEL_SNAPSHOT_QUERY_KEY),
        contentVersionId,
      );
      if (!('hash' in aprovacao)) {
        const erro = new PanelApiError({
          code: 'manifest_hash_missing',
          // A mensagem vem do MOTIVO, e não é mais uma frase só para os três
          // casos. "Recarregue o painel" não conserta peça que não sela, e
          // mandar recarregar ali gastava o tempo de quem lê antes de ele
          // descobrir que o problema é outro.
          message: aprovacao.motivo,
          requestId: newIdempotencyKey(),
          fieldErrors: null,
        });
        showToast(messageOf(erro), 'error');
        throw erro;
      }
      const hashVisto = aprovacao.hash;
      const result = await optimistic(
        (current) => ({
          ...current,
          today: {
            ...current.today,
            counters: alreadyApproved(current, contentVersionId)
              ? current.today.counters
              : {
                  ...current.today.counters,
                  awaiting: Math.max(0, current.today.counters.awaiting - 1),
                  released: current.today.counters.released + 1,
                },
            queue: current.today.queue.map((piece) =>
              piece.contentVersionId === contentVersionId
                ? {...piece, status: 'approved' as const}
                : piece,
            ),
          },
        }),
        () =>
          dataSource.approveContent({
            contentVersionId,
            idempotencyKey: newIdempotencyKey(),
            justification: ressalva,
            expectedManifestHash: hashVisto,
          }),
        () => {
          // A repetição engole o próprio erro: o caminho repetido anuncia a
          // falha de novo (toast ou diálogo), e ninguém está aguardando esta
          // promise para reagir.
          //
          // A ressalva vai junto na repetição. Sem ela, a aprovação recusada
          // por passkey voltaria SEM justificativa depois da cerimônia, e o
          // dono trocaria o `403` por um `428` sem ter feito nada errado.
          void acoesRef.current
            .approveContent?.(contentVersionId, ressalva)
            .catch(() => {
              /* já anunciado no caminho da ação */
            });
        },
      );

      patch((current) => ({
        ...current,
        today: {...current.today, counters: result.counters},
      }));
      showToast(
        'Peça liberada. O manifesto desta versão foi selado para o slot programado.',
      );
      return result;
    },
    [dataSource, optimistic, patch, queryClient, refuseWhilePaused, showToast],
  );

  const requestChange = useCallback(
    async (contentVersionId: string, comment: string) => {
      refuseWhilePaused();
      const result = await optimistic(
        (current) => ({
          ...current,
          today: {
            ...current.today,
            queue: current.today.queue.map((piece) =>
              piece.contentVersionId === contentVersionId
                ? {...piece, status: 'change_requested' as const}
                : piece,
            ),
          },
        }),
        () =>
          dataSource.requestChange({
            contentVersionId,
            comment,
            idempotencyKey: newIdempotencyKey(),
          }),
        () => {
          void acoesRef.current
            .requestChange?.(contentVersionId, comment)
            .catch(() => {
              /* já anunciado no caminho da ação */
            });
        },
      );

      patch((current) => ({
        ...current,
        today: {...current.today, counters: result.counters},
      }));
      /*
       * A MENSAGEM HONESTA de "Pedir alteração".
       *
       * A antiga dizia "uma nova versão será gerada e passará novamente pelos 7
       * portões", e nada disso acontecia: a ação registra uma decisão em
       * `content_history` e move a versão para `changes_requested`. Nenhuma
       * geração é disparada (achado F12 da auditoria de 06/09/2026). Quem
       * seguisse a tela ficaria esperando uma peça nova que ninguém pediu.
       *
       * A escolha entre EXPLICAR e ENCADEAR foi feita com o código na mão, e
       * ficou em explicar. Encadear significaria chamar `regenerateContent`
       * logo depois desta decisão, e as duas ações não são a mesma coisa:
       * regenerar exige REAUTENTICAÇÃO recente (`requireRecentReauth`, na rota),
       * descarta a versão e reabre o dia inteiro, enquanto pedir alteração é
       * uma decisão que fica registrada. Fazer o encadeamento sozinho compraria
       * uma geração paga a partir de um clique que não pede passkey, e o botão
       * "Regenerar peça" já está ao lado, para quem quiser exatamente isso.
       */
      showToast(
        'Pedido registrado no histórico da peça. Ele NÃO gera uma versão nova: para escrever outra, use "Regenerar peça".',
      );
      return result;
    },
    [dataSource, optimistic, patch, refuseWhilePaused, showToast],
  );

  const rejectContent = useCallback(
    async (contentVersionId: string, comment: string) => {
      refuseWhilePaused();
      const result = await optimistic(
        (current) => ({
          ...current,
          today: {
            ...current.today,
            queue: current.today.queue.map((piece) =>
              piece.contentVersionId === contentVersionId
                ? {...piece, status: 'rejected' as const}
                : piece,
            ),
          },
        }),
        () =>
          dataSource.rejectContent({
            contentVersionId,
            comment,
            idempotencyKey: newIdempotencyKey(),
          }),
        () => {
          void acoesRef.current
            .rejectContent?.(contentVersionId, comment)
            .catch(() => {
              /* já anunciado no caminho da ação */
            });
        },
      );

      patch((current) => ({
        ...current,
        today: {...current.today, counters: result.counters},
      }));
      showToast(
        'Peça rejeitada. Ela não será publicada, e a versão fica registrada.',
      );
      return result;
    },
    [dataSource, optimistic, patch, refuseWhilePaused, showToast],
  );

  /**
   * Regenerar tira a peça da fila e manda a esteira produzir outra.
   *
   * O otimismo REMOVE a peça, em vez de trocar o selo dela como as três
   * decisões fazem. Não é escolha de estilo: a versão que estava ali foi
   * aposentada, e a que nasceu está vazia. Deixá-la na lista com um selo novo
   * ofereceria ao dono um cartão para aprovar sobre um roteiro que não existe.
   *
   * Depois da resposta a query é INVALIDADA, e é ela quem diz a verdade: o
   * servidor acabou de mexer no dia inteiro (a execução voltou a `partial`) e o
   * retrato que a tela tinha ficou velho em mais campos do que este otimismo
   * sabe corrigir.
   */
  const regenerateContent = useCallback(
    async (contentVersionId: string, reason: string, changeTopic: boolean) => {
      refuseWhilePaused();
      const result = await optimistic(
        (current) => ({
          ...current,
          today: {
            ...current.today,
            counters: {
              ...current.today.counters,
              awaiting: Math.max(
                0,
                current.today.counters.awaiting -
                  (aguardando(current, contentVersionId) ? 1 : 0),
              ),
            },
            queue: current.today.queue.filter(
              (piece) => piece.contentVersionId !== contentVersionId,
            ),
          },
        }),
        () =>
          dataSource.regenerateContent({
            contentVersionId,
            reason,
            changeTopic,
          }),
        () => {
          void acoesRef.current
            .regenerateContent?.(contentVersionId, reason, changeTopic)
            .catch(() => {
              /* já anunciado no caminho da ação */
            });
        },
      );

      void queryClient.invalidateQueries({queryKey: PANEL_SNAPSHOT_QUERY_KEY});
      /*
       * A aba passa a ACOMPANHAR, em vez de prometer e esquecer.
       *
       * `runId` nulo é resposta honesta da API: o dia não tem execução aberta
       * para reexecutar, então a versão nova fica vazia esperando alguém abrir
       * o dia. Sem este estado, aquele caso saía com o mesmo toast do caminho
       * feliz, e a peça simplesmente nunca voltava.
       */
      setRegeneracao({
        contentVersionId,
        runId: result.runId,
        requestedAt: result.requestedAt,
        estado: result.runId === null ? 'sem_execucao' : 'aguardando',
      });
      showToast(
        result.runId === null
          ? 'Versão nova aberta, e o dia não tem execução para preenchê-la. Ela fica vazia até alguém abrir o dia.'
          : 'Peça em regeneração. A fila se atualiza sozinha quando ela voltar.',
      );
      return result;
    },
    [dataSource, optimistic, queryClient, refuseWhilePaused, showToast],
  );

  const togglePause = useCallback(async () => {
    const current = queryClient.getQueryData<PanelSnapshotDto>(
      PANEL_SNAPSHOT_QUERY_KEY,
    );
    if (!current) {
      return;
    }
    const next = !current.operation.paused;

    // A pausa é aplicada antes da resposta, de propósito: bloquear ação
    // irreversível não pode esperar a rede.
    patch((snapshotNow) => ({
      ...snapshotNow,
      operation: {
        ...snapshotNow.operation,
        paused: next,
        connection: next ? 'disconnected' : 'connected',
      },
    }));
    setToast(null);

    try {
      const operation = await dataSource.setEmergencyPause({
        paused: next,
        idempotencyKey: newIdempotencyKey(),
      });
      patch((snapshotNow) => ({...snapshotNow, operation}));
    } catch (error) {
      patch((snapshotNow) => ({
        ...snapshotNow,
        operation: current.operation,
      }));
      const problema = describeProblem(error);
      if (reauth.armado && problema.missingProof === 'passkey_reauth') {
        reauth.oferecer({
          message: problema.message,
          retry: () => {
            void acoesRef.current.togglePause?.();
          },
        });
      } else {
        showToast(messageOf(error), 'error');
      }
    }
  }, [dataSource, patch, queryClient, reauth, showToast]);

  // Preenchido a cada render, DEPOIS das definições: é o que permite ao
  // diálogo de reauth repetir a ação pelo caminho completo.
  acoesRef.current.approveContent = approveContent;
  acoesRef.current.requestChange = requestChange;
  acoesRef.current.rejectContent = rejectContent;
  acoesRef.current.regenerateContent = regenerateContent;
  acoesRef.current.togglePause = togglePause;

  const value = useMemo<PanelContextValue>(
    () => ({
      dataSource,
      snapshot,
      snapshotError,
      paused: snapshot?.operation.paused ?? false,
      toast,
      showToast,
      approveContent,
      requestChange,
      rejectContent,
      regenerateContent,
      regeneracao,
      atualizarAgora,
      togglePause,
    }),
    [
      approveContent,
      atualizarAgora,
      dataSource,
      regeneracao,
      regenerateContent,
      requestChange,
      rejectContent,
      showToast,
      snapshot,
      snapshotError,
      toast,
      togglePause,
    ],
  );

  return (
    <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
  );
};

/**
 * Por que esta peça NÃO é aprovável por esta tela, ou o hash do que seria
 * aprovado.
 *
 * Ela devolve o hash quando dá, e uma MENSAGEM quando não dá. A mensagem existe
 * porque o `null` de antes cobria coisas que pedem reações opostas, e a tela
 * dizia "recarregue o painel" para todas elas:
 *
 * - a peça já foi decidida, que é o fluxo normal e não se conserta;
 * - o manifesto não pôde ser selado, que é peça sem mídia ou sem capa, e o que
 *   resolve é a esteira terminar, não um F5;
 * - a peça não está mais na fila, que é o retrato velho e aí sim recarregar
 *   resolve.
 *
 * Nenhum caso admite chute: um hash inventado aqui viraria uma aprovação sobre
 * conteúdo que ninguém leu, que é o defeito F03 de 2026-09-06 por outro
 * caminho.
 */
const aprovacaoDoCartao = (
  snapshot: PanelSnapshotDto | undefined,
  contentVersionId: string,
): {hash: string} | {motivo: string} => {
  const piece = snapshot?.today.queue.find(
    (item) => item.contentVersionId === contentVersionId,
  );
  if (!piece) {
    return {
      motivo:
        'Esta peça não está mais na fila que a tela carregou. Recarregue o painel.',
    };
  }
  switch (piece.approval.kind) {
    case 'ready':
      return {hash: piece.approval.manifestHash};
    case 'decided':
      return {
        motivo: 'Esta peça já foi decidida. Não há o que aprovar aqui de novo.',
      };
    case 'unsealable':
      return {
        motivo:
          'A esteira não conseguiu selar o manifesto desta peça: falta mídia, capa ou política de portões. Recarregar não resolve, e o runbook editorial explica onde olhar.',
      };
  }
};

const alreadyApproved = (
  snapshot: PanelSnapshotDto,
  contentVersionId: string,
): boolean =>
  snapshot.today.queue.some(
    (piece) =>
      piece.contentVersionId === contentVersionId &&
      piece.status === 'approved',
  );

/**
 * A peça ainda contava como "aguardando" antes da regeneração?
 *
 * Só quem estava esperando decisão sai do contador de espera. Peça já aprovada
 * ou rejeitada nunca esteve nele, e descontar mesmo assim faria o número cair
 * abaixo do que a fila mostra.
 */
const aguardando = (
  snapshot: PanelSnapshotDto,
  contentVersionId: string,
): boolean =>
  snapshot.today.queue.some(
    (piece) =>
      piece.contentVersionId === contentVersionId &&
      (piece.status === 'awaiting_approval' || piece.status === 'blocked'),
  );
